import React, { useCallback, useEffect, useState } from 'react';
import { Copy, LogOut, Mail, ShieldMinus, ShieldPlus, Trash2, UserMinus, UserPlus } from 'lucide-react';
import { apiClient } from '../../../lib/api';
import { useToast } from '../../../hooks/useToast';
import { useAuth } from '../../../contexts/AuthContext';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Badge } from '../../ui/Badge';
import { Spinner } from '../../ui/Spinner';
import { Modal } from '../../ui/Modal';
import { formatDateOnly } from '../../../utils/dateUtils';
import { getErrorDetail } from '../../../utils/errorUtils';
import type { WorkspaceInvite, WorkspaceMember } from '../../../types/workspace';

export const MembersSection: React.FC = () => {
  const { showToast } = useToast();
  const { user } = useAuth();
  const currentUserId = user?.id;
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [invites, setInvites] = useState<WorkspaceInvite[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [roleChangingId, setRoleChangingId] = useState<string | null>(null);
  const [isLeaving, setIsLeaving] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveConfirmText, setLeaveConfirmText] = useState('');
  // The one-time shareable link from the most recent invite (no email delivery yet).
  const [lastInviteLink, setLastInviteLink] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [m, inv] = await Promise.all([
        apiClient.getWorkspaceMembers(),
        apiClient.listWorkspaceInvites(),
      ]);
      setMembers(m);
      setInvites(inv);
    } catch (error) {
      console.error('Error loading workspace members:', error);
      showToast('Failed to load members. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = inviteEmail.trim();
    if (!email) return;
    setIsInviting(true);
    try {
      const invite = await apiClient.createWorkspaceInvite(email);
      setInviteEmail('');
      setLastInviteLink(invite.accept_url ?? null);
      showToast('Invite created', 'success');
      await load();
    } catch (error) {
      showToast(getErrorDetail(error, 'Failed to create invite'), 'error');
    } finally {
      setIsInviting(false);
    }
  };

  const handleRevoke = async (inviteId: string) => {
    setRevokingId(inviteId);
    try {
      await apiClient.revokeWorkspaceInvite(inviteId);
      showToast('Invite revoked', 'success');
      await load();
    } catch {
      showToast('Failed to revoke invite', 'error');
    } finally {
      setRevokingId(null);
    }
  };

  const handleRemove = async (member: WorkspaceMember) => {
    const label = member.name || member.email || 'this member';
    if (!window.confirm(`Remove ${label} from the workspace? They'll lose access to its data.`)) {
      return;
    }
    setRemovingId(member.user_id);
    try {
      await apiClient.removeWorkspaceMember(member.user_id);
      showToast('Member removed', 'success');
      await load();
    } catch (error) {
      showToast(getErrorDetail(error, 'Failed to remove member'), 'error');
    } finally {
      setRemovingId(null);
    }
  };

  const handleRoleChange = async (member: WorkspaceMember, role: 'owner' | 'member') => {
    setRoleChangingId(member.user_id);
    try {
      await apiClient.updateWorkspaceMemberRole(member.user_id, role);
      showToast(role === 'owner' ? 'Member promoted to owner' : 'Owner demoted to member', 'success');
      await load();
    } catch (error) {
      showToast(getErrorDetail(error, 'Failed to update role'), 'error');
    } finally {
      setRoleChangingId(null);
    }
  };

  const handleLeave = async () => {
    setShowLeaveModal(false);
    setIsLeaving(true);
    try {
      await apiClient.leaveWorkspace();
      showToast('You left the workspace', 'success');
      // Active workspace changed — reload so the app re-fetches under the new workspace.
      window.location.assign('/datasets');
    } catch (error) {
      showToast(getErrorDetail(error, 'Failed to leave workspace'), 'error');
      setIsLeaving(false);
    }
  };

  const copyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      showToast('Invite link copied', 'success');
    } catch {
      showToast('Could not copy link', 'error');
    }
  };

  if (isLoading) {
    return <Spinner size="md" text="Loading members…" center />;
  }

  const currentUserRole = members.find((m) => m.user_id === currentUserId)?.role;
  const isOwner = currentUserRole === 'owner';

  return (
    <div className="space-y-6">
      {/* Invite form — owner only */}
      <div>
        <h3 className="text-[13px] font-semibold text-neutral-900 mb-3">Invite a teammate</h3>
        {isOwner ? (
          <Card padding="md">
            <form onSubmit={handleInvite} className="flex items-end gap-3">
              <div className="flex-1">
                <Input
                  label="Email address"
                  type="email"
                  placeholder="teammate@company.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  fullWidth
                  inputSize="sm"
                />
              </div>
              <Button
                type="submit"
                variant="primary"
                size="sm"
                loading={isInviting}
                leftIcon={<UserPlus className="w-4 h-4" />}
              >
                Invite
              </Button>
            </form>
            {lastInviteLink && (
              <div className="mt-3 flex items-center gap-2 rounded-lg bg-neutral-50 border border-neutral-200 px-3 py-2">
                <span className="flex-1 truncate text-xs text-neutral-600">{lastInviteLink}</span>
                <Button
                  variant="ghost"
                  size="xs"
                  leftIcon={<Copy className="w-3.5 h-3.5" />}
                  onClick={() => copyLink(lastInviteLink)}
                >
                  Copy link
                </Button>
              </div>
            )}
            <p className="mt-2 text-2xs text-neutral-400">
              Share this link with your teammate — they accept it while signed in with the invited email.
            </p>
          </Card>
        ) : (
          <Card padding="md">
            <p className="text-[13px] text-neutral-500">
              Only the workspace owner can invite members.
            </p>
          </Card>
        )}
      </div>

      {/* Pending invites */}
      {invites.length > 0 && (
        <div>
          <h3 className="text-[13px] font-semibold text-neutral-900 mb-3">Pending invites</h3>
          <div className="space-y-2">
            {invites.map((inv) => (
              <Card key={inv.id} padding="sm">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <Mail className="w-4 h-4 text-neutral-400 shrink-0" />
                    <span className="truncate text-[13px] text-neutral-900">{inv.email}</span>
                    <Badge variant="warning" size="sm" shape="pill">Pending</Badge>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-2xs text-neutral-400">Expires {formatDateOnly(inv.expires_at)}</span>
                    {isOwner && (
                      <Button
                        variant="ghost"
                        size="xs"
                        loading={revokingId === inv.id}
                        leftIcon={<Trash2 className="w-3.5 h-3.5" />}
                        onClick={() => handleRevoke(inv.id)}
                        aria-label={`Revoke invite for ${inv.email}`}
                      >
                        Revoke
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Members */}
      <div>
        <h3 className="text-[13px] font-semibold text-neutral-900 mb-3">Members</h3>
        <div className="space-y-2">
          {members.map((m) => {
            const isSelf = m.user_id === currentUserId;
            const canRemove = isOwner && !isSelf && m.role !== 'owner';
            const canChangeRole = isOwner && m.role !== 'owner';
            const canDemote = isOwner && m.role === 'owner' && !isSelf;
            const label = m.name || m.email || 'member';
            return (
              <Card key={m.user_id} padding="sm">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-[13px] font-medium text-neutral-900">
                      {m.name || m.email || m.user_id}
                      {isSelf && <span className="text-neutral-400 font-normal"> (You)</span>}
                    </p>
                    {m.email && m.name && (
                      <p className="truncate text-2xs text-neutral-400">{m.email}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Badge variant={m.role === 'owner' ? 'primary' : 'neutral'} size="sm" shape="pill">
                      {m.role}
                    </Badge>
                    {canChangeRole && (
                      <Button
                        variant="ghost"
                        size="xs"
                        loading={roleChangingId === m.user_id}
                        leftIcon={<ShieldPlus className="w-3.5 h-3.5" />}
                        onClick={() => handleRoleChange(m, 'owner')}
                        aria-label={`Make ${label} an owner`}
                      >
                        Make owner
                      </Button>
                    )}
                    {canDemote && (
                      <Button
                        variant="ghost"
                        size="xs"
                        loading={roleChangingId === m.user_id}
                        leftIcon={<ShieldMinus className="w-3.5 h-3.5" />}
                        onClick={() => handleRoleChange(m, 'member')}
                        aria-label={`Remove owner from ${label}`}
                      >
                        Remove owner
                      </Button>
                    )}
                    {canRemove && (
                      <Button
                        variant="ghost"
                        size="xs"
                        loading={removingId === m.user_id}
                        leftIcon={<UserMinus className="w-3.5 h-3.5" />}
                        onClick={() => handleRemove(m)}
                        aria-label={`Remove ${label}`}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Leave workspace — only meaningful in a shared workspace */}
      {members.length > 1 && (
        <div className="pt-2 border-t border-neutral-100">
          <Button
            variant="ghost"
            size="sm"
            loading={isLeaving}
            leftIcon={<LogOut className="w-4 h-4" />}
            onClick={() => {
              setLeaveConfirmText('');
              setShowLeaveModal(true);
            }}
            aria-label="Leave this workspace"
          >
            Leave workspace
          </Button>
        </div>
      )}

      {/* Type-to-confirm leaving (destructive: you lose your own access) */}
      <Modal isOpen={showLeaveModal} onClose={() => setShowLeaveModal(false)} title="Leave workspace">
        <div className="space-y-4">
          <p className="text-[13px] text-neutral-600">
            You'll lose access to this workspace's shared transcripts and customer data, and be
            moved to a new empty workspace. This can't be undone — you'd need a fresh invite to
            rejoin.
          </p>
          <div>
            <label className="block text-[11px] font-medium text-neutral-500 mb-1.5">
              Type <span className="font-semibold text-neutral-900">confirm</span> to continue
            </label>
            <Input
              value={leaveConfirmText}
              onChange={(e) => setLeaveConfirmText(e.target.value)}
              placeholder="confirm"
              fullWidth
              inputSize="sm"
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setShowLeaveModal(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              loading={isLeaving}
              disabled={leaveConfirmText.trim().toLowerCase() !== 'confirm'}
              onClick={handleLeave}
            >
              Leave workspace
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
